import React from "react";
import { Loader2 } from "lucide-react";

import {
  getApprovalPipelineStatusLabel,
  resultMessage,
  type ApprovalPipelineState,
} from "@/lib/attributeReview/approvalPipeline";
import { cn } from "@/lib/utils";

export interface ApprovalPipelineActivityPanelProps {
  jobs: ApprovalPipelineState[];
  sidebarCollapsed: boolean;
  hasStickyFooter?: boolean;
}

function truncateJobId(jobId: string | null | undefined): string {
  if (!jobId) return "pending";
  return jobId.length <= 8 ? jobId : jobId.slice(0, 8);
}

export const ApprovalPipelineActivityPanel: React.FC<
  ApprovalPipelineActivityPanelProps
> = ({ jobs, sidebarCollapsed, hasStickyFooter = false }) => {
  if (jobs.length === 0) return null;

  return (
    <div
      className={cn(
        "fixed z-30 w-[340px] max-w-[calc(100vw-2rem)] transition-all duration-200",
        sidebarCollapsed ? "left-auto right-4" : "left-auto right-4",
        hasStickyFooter ? "bottom-24" : "bottom-4",
      )}
    >
      <div className="overflow-hidden rounded-lg border border-indigo-200 bg-white shadow-xl dark:border-indigo-900/70 dark:bg-slate-900">
        <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3 dark:border-slate-800">
          <div className="flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin text-indigo-500" />
            <div>
              <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                Background approvals
              </p>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                {jobs.length} active {jobs.length === 1 ? "job" : "jobs"}
              </p>
            </div>
          </div>
          <span className="inline-flex min-w-6 items-center justify-center rounded-full bg-indigo-100 px-2 py-1 text-xs font-semibold text-indigo-700 dark:bg-indigo-900/50 dark:text-indigo-200">
            {jobs.length}
          </span>
        </div>

        <div className="max-h-72 overflow-y-auto px-3 py-2">
          <div className="space-y-2">
            {jobs.map((job) => {
              const message =
                job.message ??
                job.fieldResults.map(resultMessage).find(Boolean) ??
                job.valueResults.map(resultMessage).find(Boolean) ??
                "Queued in the approval pipeline.";

              return (
                <div
                  key={`${job.syncJobId ?? job.pipelineId ?? job.mappingSessionId}-${job.occurredAt ?? "live"}`}
                  className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 dark:border-slate-800 dark:bg-slate-950/40"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium text-slate-900 dark:text-slate-100">
                        {getApprovalPipelineStatusLabel(job.status)}
                      </p>
                      <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                        Job {truncateJobId(job.syncJobId)}
                      </p>
                    </div>
                    <Loader2 className="mt-0.5 h-3.5 w-3.5 shrink-0 animate-spin text-indigo-500" />
                  </div>
                  <p className="mt-2 text-xs leading-5 text-slate-600 dark:text-slate-300">
                    {message}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};
